import type { IClass } from '@/types/class';
import type {
  ITeacher,
  ITeacherDetails,
  TTeachersClassesTab,
} from '@/types/teacher';

import { enqueueSnackbar } from 'notistack';
import { useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IconBan,
  IconEye,
  IconEdit,
  IconPlus,
  IconTrash,
  IconSearch,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import ListSkeleton from '@/components/SkeletonLoader/ListSkeleton';
import CustomTable from '@/components/Table';
import { Tabs } from '@/components/Tabs';
import { EBlockUnblock, ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { removeClass, getClassList } from '@/service/classService';
import {
  removeTeacher,
  getTeacherList,
  blockUnblockUser,
  getTeacherClasses,
} from '@/service/teacherService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, formatLastActive } from '@/utils';

import AddClassDrawer from './components/AddClassDrawer';
import AddTeacherDrawer from './components/AddTeacherDrawer';

const CLASSES_COLUMN_KEY = 'teachers-classes.teachers.columns.classes';
const ACTIONS_BLOCK = 'common.actions.block';
const ACTIONS_UNBLOCK = 'common.actions.unblock';
const REMOVE_ACTION_KEY = 'common.actions.remove';
const REMOVE_CLASS_KEY = 'remove-class';
const BLOCK_CLASS_KEY = 'block-class';

type TConfirmModal = {
  open: boolean;
  type: 'block' | 'remove' | typeof REMOVE_CLASS_KEY | typeof BLOCK_CLASS_KEY;
  teacher: ITeacher | null;
  classItem: IClass | null;
};

type TConfirmModalsProps = {
  confirmModal: TConfirmModal;
  setConfirmModal: React.Dispatch<React.SetStateAction<TConfirmModal>>;
  handleBlock: () => void;
  handleRemoveTeacher: () => void;
  handleBlockClass: () => void;
  handleRemoveClass: () => void;
  formatText: (key: string) => string;
};

const getActivityMode = (
  confirmModal: TConfirmModal,
  targetType: TConfirmModal['type']
): 'visible' | 'hidden' =>
  confirmModal.open && confirmModal.type === targetType ? 'visible' : 'hidden';

const ConfirmationModals = ({
  confirmModal,
  setConfirmModal,
  handleBlock,
  handleRemoveTeacher,
  handleBlockClass,
  handleRemoveClass,
  formatText,
}: TConfirmModalsProps) => {
  const setOpen = (open: boolean) =>
    setConfirmModal((prev) => ({ ...prev, open }));

  return (
    <>
      <Activity mode={getActivityMode(confirmModal, 'block')}>
        <ConfirmationModal
          isOpen={confirmModal.open && confirmModal.type === 'block'}
          setIsOpen={setOpen}
          title={formatText(
            confirmModal.teacher?.status === 'active'
              ? 'teachers-classes.block-teacher.title'
              : 'teachers-classes.unblock-teacher.title'
          )}
          descriptionPrefix={formatText(
            confirmModal.teacher?.status === 'active'
              ? 'common.confirmation.block-prefix'
              : 'common.confirmation.unblock-prefix'
          )}
          descriptionSuffix={
            confirmModal.teacher?.status === 'active'
              ? 'teachers-classes.block-teacher.suffix'
              : undefined
          }
          entityName={confirmModal.teacher?.name}
          isDestructive
          confirmLabel={formatText(
            confirmModal.teacher?.status === 'active'
              ? 'teachers-classes.block-teacher.confirm'
              : 'teachers-classes.unblock-teacher.confirm'
          )}
          onConfirm={handleBlock}
        />
      </Activity>

      <Activity mode={getActivityMode(confirmModal, 'remove')}>
        <ConfirmationModal
          isOpen={confirmModal.open && confirmModal.type === 'remove'}
          setIsOpen={setOpen}
          title="teachers-classes.remove-teacher.title"
          descriptionPrefix="teachers-classes.remove-teacher.descriptionPrefix"
          descriptionSuffix="teachers-classes.remove-teacher.descriptionSuffix"
          entityName={confirmModal.teacher?.name}
          confirmLabel={formatText('teachers-classes.remove-teacher.confirm')}
          onConfirm={handleRemoveTeacher}
          isDestructive
        />
      </Activity>

      <Activity mode={getActivityMode(confirmModal, BLOCK_CLASS_KEY)}>
        <ConfirmationModal
          isOpen={confirmModal.open && confirmModal.type === BLOCK_CLASS_KEY}
          setIsOpen={setOpen}
          title={
            confirmModal.classItem?.status === 'active'
              ? 'teachers-classes.block-class.title'
              : 'teachers-classes.unblock-class.title'
          }
          descriptionPrefix={
            confirmModal.classItem?.status === 'active'
              ? 'common.confirmation.block-prefix'
              : 'common.confirmation.unblock-prefix'
          }
          descriptionSuffix={
            confirmModal.classItem?.status === 'active'
              ? 'teachers-classes.block-class.suffix'
              : ''
          }
          entityName={confirmModal.classItem?.name}
          isDestructive={confirmModal.classItem?.status === 'active'}
          confirmLabel={formatText(
            confirmModal.classItem?.status === 'active'
              ? 'teachers-classes.block-class.confirm'
              : 'teachers-classes.unblock-class.confirm'
          )}
          onConfirm={handleBlockClass}
        />
      </Activity>

      <Activity mode={getActivityMode(confirmModal, REMOVE_CLASS_KEY)}>
        <ConfirmationModal
          isOpen={confirmModal.open && confirmModal.type === REMOVE_CLASS_KEY}
          setIsOpen={setOpen}
          title="teachers-classes.remove-class.title"
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix="teachers-classes.remove-class.suffix"
          entityName={confirmModal.classItem?.name}
          confirmLabel={formatText('teachers-classes.remove-class.confirm')}
          onConfirm={handleRemoveClass}
          isDestructive
        />
      </Activity>
    </>
  );
};

const getNextSortDir = (currentDir: string): string => {
  if (currentDir === 'ASC') return 'DESC';
  if (currentDir === 'DESC') return '';
  return 'ASC';
};

const TeachersAndClasses = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const navigate = useNavigate();
  const { settings } = useSettingsStore();
  const language = settings?.language ?? 'en';
  const pageTitle = formatText('teachers-classes.title');

  const {
    isScreenLoading,
    isTableLoading,
    setScreenLoading,
    setDrawerLoading,
    setTableLoading,
  } = useSkeletonStore();

  useEffect(() => {
    setTitle(pageTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: TTeachersClassesTab =
    rawTab === 'classes' ? 'classes' : 'teachers';
  const setActiveTab = (tab: TTeachersClassesTab) =>
    setSearchParams({ tab }, { replace: true });

  // Form for search inputs
  const { register, watch } = useForm({
    defaultValues: {
      teacherSearch: '',
      classSearch: '',
    },
  });

  const teacherSearch = watch('teacherSearch');
  const classSearch = watch('classSearch');
  const debouncedTeacherSearch = useDebounce(teacherSearch, 500);
  const debouncedClassSearch = useDebounce(classSearch, 500);

  const [teachers, setTeachers] = useState<ITeacher[]>([]);
  const [teacherTotal, setTeacherTotal] = useState(0);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherSortField, setTeacherSortField] = useState('');
  const [teacherSortDir, setTeacherSortDir] = useState('');

  const [classes, setClasses] = useState<IClass[]>([]);
  const [classTotal, setClassTotal] = useState(0);
  const [classPage, setClassPage] = useState(1);
  const [classSortField, setClassSortField] = useState('');
  const [classSortDir, setClassSortDir] = useState('');

  const [isAddTeacherOpen, setIsAddTeacherOpen] = useState(false);
  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<ITeacherDetails | null>(
    null
  );
  const [editingClass, setEditingClass] = useState<IClass | null>(null);

  const [confirmModal, setConfirmModal] = useState<TConfirmModal>({
    open: false,
    type: 'remove',
    teacher: null,
    classItem: null,
  });

  const fetchTeachers = async () => {
    if (teachers.length === 0 && !debouncedTeacherSearch) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const query: Record<string, unknown> = {
        offset: (teacherPage - 1) * 10,
        limit: 10,
      };
      if (debouncedTeacherSearch) query.search = debouncedTeacherSearch;
      if (teacherSortField && teacherSortDir) {
        query[`order[${teacherSortField}]`] = teacherSortDir;
      }

      const res = await getTeacherList(query);
      if (res?.data) {
        setTeachers(res.data ?? []);
        setTeacherTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    setTeacherPage(1);
  }, [debouncedTeacherSearch]);

  useEffect(() => {
    if (activeTab === 'teachers') {
      fetchTeachers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teacherPage,
    debouncedTeacherSearch,
    teacherSortField,
    teacherSortDir,
    activeTab,
  ]);

  // ── Fetch classes ───────────────────────────────────────────────────────

  const fetchClasses = async () => {
    if (classes.length === 0 && !debouncedClassSearch) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const query: Record<string, unknown> = {
        offset: (classPage - 1) * 10,
        limit: 10,
      };
      if (debouncedClassSearch) query.search = debouncedClassSearch;
      if (classSortField && classSortDir) {
        query[`order[${classSortField}]`] = classSortDir;
      }

      const res = await getClassList(query);
      if (res?.data) {
        setClasses(res.data ?? []);
        setClassTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    setClassPage(1);
  }, [debouncedClassSearch]);

  useEffect(() => {
    if (activeTab === 'classes') {
      fetchClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classPage,
    debouncedClassSearch,
    classSortField,
    classSortDir,
    activeTab,
  ]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleTeacherSort = (key: string) => {
    if (teacherSortField === key) {
      const next = getNextSortDir(teacherSortDir);
      setTeacherSortField(next ? teacherSortField : '');
      setTeacherSortDir(next);
    } else {
      setTeacherSortField(key);
      setTeacherSortDir('ASC');
    }
  };

  const handleClassSort = (key: string) => {
    if (classSortField === key) {
      const next = getNextSortDir(classSortDir);
      setClassSortField(next ? classSortField : '');
      setClassSortDir(next);
    } else {
      setClassSortField(key);
      setClassSortDir('ASC');
    }
  };

  const handleBlock = async () => {
    if (!confirmModal.teacher) return;
    const isCurrentlyActive = confirmModal.teacher.status === 'active';

    try {
      const response = await blockUnblockUser(
        confirmModal.teacher.id,
        EBlockUnblock.TEACHER,
        { is_block: isCurrentlyActive }
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({
          open: false,
          type: 'block',
          teacher: null,
          classItem: null,
        });
        fetchTeachers();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleBlockClass = async () => {
    if (!confirmModal.classItem) return;
    const isCurrentlyActive = confirmModal.classItem.status === 'active';

    try {
      const response = await blockUnblockUser(
        confirmModal.classItem.id,
        EBlockUnblock.CLASS,
        { is_block: isCurrentlyActive }
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({
          open: false,
          type: BLOCK_CLASS_KEY,
          teacher: null,
          classItem: null,
        });
        fetchClasses();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveTeacher = async () => {
    if (!confirmModal.teacher) return;
    try {
      const response = await removeTeacher(
        confirmModal.teacher.id,
        ERestoreEntityType.TEACHER
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({
          open: false,
          type: 'remove',
          teacher: null,
          classItem: null,
        });
        fetchTeachers();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveClass = async () => {
    if (!confirmModal.classItem) return;
    try {
      const response = await removeClass(confirmModal.classItem.id);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setConfirmModal({
          open: false,
          type: REMOVE_CLASS_KEY,
          teacher: null,
          classItem: null,
        });
        fetchClasses();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Teacher columns ──────────────────────────────────────────────────────

  const handleTeacherView = (recordId: string) =>
    navigate(`/school/teachers-classes/teacher-details/${recordId}`);

  const teacherColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'teachers-classes.teachers.columns.teacher-name',
        sorter: true,
        handleSort: () => handleTeacherSort('name'),
        width: '200px',
        render: (record: ITeacher) => (
          <div className="flex items-center gap-3">
            <UserAvatarList
              user={record.name}
              image={getAssetUrl(record?.profile_pic)}
              className="w-10! h-10 min-w-10 object-cover"
            />
            <span className="text-neutral-primary font-medium text-sm truncate max-w-40">
              {record.name}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        title: 'teachers-classes.teachers.columns.email-id',
        sorter: true,
        handleSort: () => handleTeacherSort('email'),
        width: '220px',
        render: (record: ITeacher) => (
          <span className="text-neutral-secondary text-sm">{record.email}</span>
        ),
      },
      {
        key: 'total_classes',
        title: CLASSES_COLUMN_KEY,
        sorter: true,
        handleSort: () => handleTeacherSort('total_classes'),
        align: 'start' as const,
        width: '100px',
        render: (record: ITeacher) => (
          <span className="text-indigo-600 font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-indigo-50 w-fit">
            {record.total_classes}
          </span>
        ),
      },
      {
        key: 'total_lessons',
        title: 'teachers-classes.teachers.columns.lessons-created',
        sorter: true,
        handleSort: () => handleTeacherSort('total_lessons'),
        align: 'start' as const,
        width: '140px',
        render: (record: ITeacher) => (
          <span className="text-pink-600 font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-pink-50 w-fit">
            {record.total_lessons}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'teachers-classes.teachers.columns.active-students',
        sorter: true,
        handleSort: () => handleTeacherSort('active_students'),
        align: 'start' as const,
        width: '130px',
        render: (record: ITeacher) => (
          <span className="text-success-primary font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-green-50 w-fit">
            {record.active_students}
          </span>
        ),
      },
      {
        key: 'avg_completion',
        title: 'teachers-classes.teachers.columns.avg-completion',
        sorter: true,
        handleSort: () => handleTeacherSort('avg_completion'),
        width: '180px',
        render: (record: ITeacher) => (
          <ProgressBar value={record.avg_completion ?? 0} />
        ),
      },
      {
        key: 'last_update',
        title: 'teachers-classes.teachers.columns.last-update',
        sorter: true,
        handleSort: () => handleTeacherSort('last_update'),
        width: '120px',
        render: (record: ITeacher) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {formatLastActive(Number(record.last_update), formatText)}
          </span>
        ),
      },
      {
        key: 'status',
        title: 'teachers-classes.teachers.columns.status',
        width: '120px',
        sorter: true,
        handleSort: () => handleTeacherSort('status'),
        render: (record: ITeacher) => {
          const isActive = record.status === 'active';
          return (
            <span
              className={`text-xs font-semibold py-0.5 px-2.5 rounded-full ${
                isActive
                  ? 'text-success-primary bg-green-50'
                  : 'bg-warning-light-100 text-warning-secondary'
              }`}
            >
              {isActive
                ? formatText('common.actions.active')
                : formatText('common.actions.inactive')}
            </span>
          );
        },
      },
      {
        key: 'action',
        title: 'teachers-classes.teachers.columns.action',
        align: 'start' as const,
        width: '70px',
        render: (record: ITeacher) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => handleTeacherView(record?.id),
              },
              {
                key: 'edit',
                label: 'common.actions.edit',
                icon: <IconEdit size={18} />,
                onClick: async () => {
                  try {
                    setEditingTeacher(null); // Ensure null before fetching
                    setIsAddTeacherOpen(true);
                    setDrawerLoading(true);
                    const res = await getTeacherClasses(record.id);
                    if (res?.data) {
                      setEditingTeacher(res.data);
                    }
                  } catch (error) {
                    console.error(error);
                  } finally {
                    setDrawerLoading(false);
                  }
                },
              },
              {
                key: 'block',
                label:
                  record.status === 'active' ? ACTIONS_BLOCK : ACTIONS_UNBLOCK,
                icon: <IconBan size={18} />,
                onClick: () =>
                  setConfirmModal({
                    open: true,
                    type: 'block',
                    teacher: record,
                    classItem: null,
                  }),
              },
              {
                key: 'remove',
                label: REMOVE_ACTION_KEY,
                icon: <IconTrash size={18} />,
                isDanger: true,
                onClick: () =>
                  setConfirmModal({
                    open: true,
                    type: 'remove',
                    teacher: record,
                    classItem: null,
                  }),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teacherSortField, teacherSortDir]
  );

  // ── Class columns ───────────────────────────────────────────────────────

  const handleClassView = (record: IClass) =>
    navigate(`/school/teachers-classes/class-details/${record.id}`, {
      state: { className: record.name },
    });

  const classColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'teachers-classes.classes.columns.assigned-class',
        sorter: true,
        handleSort: () => handleClassSort('name'),
        width: '200px',
        render: (record: IClass) => (
          <span className="text-neutral-primary font-medium text-sm">
            {record.name}
          </span>
        ),
      },
      {
        key: 'total_teachers',
        title: 'teachers-classes.classes.columns.total-teachers',
        sorter: true,
        handleSort: () => handleClassSort('total_teachers'),
        align: 'start' as const,
        width: '140px',
        render: (record: IClass) => (
          <span className="text-orange-600 font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-orange-50 w-fit">
            {record.total_teachers}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'teachers-classes.classes.columns.active-students',
        sorter: true,
        handleSort: () => handleClassSort('active_students'),
        align: 'start' as const,
        width: '140px',
        render: (record: IClass) => (
          <span className="text-success-primary font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-green-50 w-fit">
            {record.active_students}
          </span>
        ),
      },
      {
        key: 'lessons',
        title: 'teachers-classes.classes.columns.lessons',
        sorter: true,
        handleSort: () => handleClassSort('lessons'),
        align: 'start' as const,
        width: '100px',
        render: (record: IClass) => (
          <span className="text-pink-600 font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-pink-50 w-fit">
            {record.lessons}
          </span>
        ),
      },
      {
        key: 'avg_completion',
        title: 'teachers-classes.classes.columns.avg-completion',
        sorter: true,
        handleSort: () => handleClassSort('avg_completion'),
        width: '200px',
        render: (record: IClass) => (
          <ProgressBar value={record.avg_completion} />
        ),
      },
      {
        key: 'last_update',
        title: 'teachers-classes.classes.columns.last-update',
        sorter: true,
        handleSort: () => handleClassSort('last_update'),
        width: '120px',
        render: (record: IClass) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {formatLastActive(record.last_update, formatText)}
          </span>
        ),
      },
      {
        key: 'status',
        title: 'teachers-classes.classes.columns.status',
        width: '120px',
        sorter: true,
        handleSort: () => handleClassSort('status'),
        render: (record: IClass) => {
          const isActive = record.status === 'active';
          return (
            <span
              className={`text-xs font-semibold py-0.5 px-2.5 rounded-full ${
                isActive
                  ? 'text-success-primary bg-green-50'
                  : 'bg-warning-light-100 text-warning-secondary'
              }`}
            >
              {isActive
                ? formatText('common.actions.active')
                : formatText('common.actions.inactive')}
            </span>
          );
        },
      },
      {
        key: 'action',
        title: 'teachers-classes.classes.columns.action',
        align: 'start' as const,
        width: '70px',
        render: (record: IClass) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => handleClassView(record),
              },
              {
                key: 'edit',
                label: 'common.actions.edit',
                icon: <IconEdit size={18} />,
                onClick: () => {
                  setEditingClass(record);
                  setIsAddClassOpen(true);
                },
              },
              {
                key: 'block',
                label:
                  record.status === 'active' ? ACTIONS_BLOCK : ACTIONS_UNBLOCK,
                icon: <IconBan size={18} />,
                onClick: () =>
                  setConfirmModal({
                    open: true,
                    type: BLOCK_CLASS_KEY,
                    teacher: null,
                    classItem: record,
                  }),
              },
              {
                key: 'remove',
                label: REMOVE_ACTION_KEY,
                icon: <IconTrash size={18} />,
                isDanger: true,
                onClick: () =>
                  setConfirmModal({
                    open: true,
                    type: REMOVE_CLASS_KEY,
                    teacher: null,
                    classItem: record,
                  }),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classSortField, classSortDir]
  );

  // ── Tab options ─────────────────────────────────────────────────────────

  const tabOptions = [
    {
      label: formatText('teachers-classes.tabs.teachers'),
      value: 'teachers' as TTeachersClassesTab,
    },
    {
      label: formatText('teachers-classes.tabs.classes'),
      value: 'classes' as TTeachersClassesTab,
    },
  ];

  // ── Empty state checks ─────────────────────────────────────────────────

  const hasNoTeachers =
    !isScreenLoading && teachers.length === 0 && !debouncedTeacherSearch;
  const isTeacherEmptySearch =
    !isScreenLoading && teachers.length === 0 && !!debouncedTeacherSearch;

  const hasNoClasses =
    !isScreenLoading && classes.length === 0 && !debouncedClassSearch;
  const isClassEmptySearch =
    !isScreenLoading && classes.length === 0 && !!debouncedClassSearch;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div
      className="flex flex-col gap-6 h-full"
      data-testid="teachers-classes-page-container"
    >
      {/* Tabs */}
      <Tabs<TTeachersClassesTab>
        options={tabOptions}
        value={activeTab}
        onChange={setActiveTab}
        dataTestId="teachers-classes-tabs"
      />

      {/* ─── Teachers Tab ─────────────────────────────────────────────── */}
      {activeTab === 'teachers' && (
        <div data-testid="teachers-tab-content">
          {isScreenLoading ? (
            <ListSkeleton columns={teacherColumns} />
          ) : (
            <div className="flex flex-col gap-5 h-full">
              {!hasNoTeachers && (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-1 min-w-0 ps-1">
                      <Input
                        name="teacherSearch"
                        placeholder={formatText(
                          'teachers-classes.teachers.search-placeholder'
                        )}
                        leftIcon={{
                          icon: (
                            <IconSearch
                              size={18}
                              className="text-neutral-400"
                            />
                          ),
                        }}
                        register={register}
                        className="max-w-70"
                        inputClassName="py-[13px]"
                        dataTestId="teacher-search-input"
                      />
                    </div>

                    <Button
                      variant="primary"
                      label={formatText(
                        'teachers-classes.teachers.add-teacher'
                      )}
                      icon={<IconPlus size={18} />}
                      className="h-11 px-5"
                      onClick={() => {
                        setEditingTeacher(null);
                        setIsAddTeacherOpen(true);
                      }}
                      dataTestId="add-teacher-button"
                    />
                  </div>
                  <h2 className="text-lg font-semibold text-neutral-primary">
                    {formatText('teachers-classes.teachers.overview')}
                  </h2>
                </>
              )}

              <CustomTable<ITeacher>
                dataSource={teachers}
                columns={teacherColumns}
                rowKey="id"
                total={teacherTotal}
                currentPage={teacherPage}
                setCurrentPage={setTeacherPage}
                isEmptySearch={isTeacherEmptySearch}
                fieldName={teacherSortField}
                sortDirection={teacherSortDir}
                isEmpty={hasNoTeachers}
                showHeader={!hasNoTeachers}
                hidePagination={hasNoTeachers}
                emptyImgSrc="/not-found.svg"
                emptyTitle={formatText('teachers-classes.teachers.empty-title')}
                emptyDescription={formatText(
                  'teachers-classes.teachers.empty-description'
                )}
                emptyButtonText={formatText(
                  'teachers-classes.teachers.add-teacher'
                )}
                emptyButtonIcon={<IconPlus size={18} />}
                onEmptyBtnClick={() => {
                  setEditingTeacher(null);
                  setIsAddTeacherOpen(true);
                }}
                isLoading={isTableLoading}
                dataTestId="teachers-table"
                onRowClick={(id: string) => handleTeacherView(id)}
              />
            </div>
          )}
        </div>
      )}

      {/* ─── Classes Tab ──────────────────────────────────────────────── */}
      {activeTab === 'classes' && (
        <div data-testid="classes-tab-content">
          {isScreenLoading ? (
            <ListSkeleton columns={classColumns} />
          ) : (
            <div className="flex flex-col gap-5 h-full">
              {!hasNoClasses && (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-1 min-w-0 ps-1">
                      <Input
                        name="classSearch"
                        placeholder={formatText(
                          'teachers-classes.classes.search-placeholder'
                        )}
                        leftIcon={{
                          icon: (
                            <IconSearch
                              size={18}
                              className="text-neutral-400"
                            />
                          ),
                        }}
                        register={register}
                        className="max-w-70"
                        inputClassName="py-[13px]"
                        dataTestId="class-search-input"
                      />
                    </div>

                    <Button
                      variant="primary"
                      label={formatText('teachers-classes.classes.add-classes')}
                      icon={<IconPlus size={18} />}
                      className="h-11 px-5"
                      onClick={() => {
                        setEditingClass(null);
                        setIsAddClassOpen(true);
                      }}
                      dataTestId="add-class-button"
                    />
                  </div>
                  <h2 className="text-lg font-semibold text-neutral-primary">
                    {formatText('teachers-classes.classes.overview')}
                  </h2>
                </>
              )}

              <CustomTable<IClass>
                dataSource={classes}
                columns={classColumns}
                rowKey="id"
                total={classTotal}
                currentPage={classPage}
                setCurrentPage={setClassPage}
                isEmptySearch={isClassEmptySearch}
                fieldName={classSortField}
                sortDirection={classSortDir}
                isEmpty={hasNoClasses}
                showHeader={!hasNoClasses}
                hidePagination={hasNoClasses}
                emptyImgSrc="/not-found.svg"
                emptyTitle={formatText('teachers-classes.classes.empty-title')}
                emptyDescription={formatText(
                  'teachers-classes.classes.empty-description'
                )}
                emptyButtonText={formatText(
                  'teachers-classes.classes.add-classes'
                )}
                emptyButtonIcon={<IconPlus size={18} />}
                onEmptyBtnClick={() => setIsAddClassOpen(true)}
                isLoading={isTableLoading}
                dataTestId="classes-table"
                onRowClick={(id: string) => {
                  const classItem = classes.find((c) => c.id === id);
                  if (classItem) {
                    handleClassView(classItem);
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModals
        confirmModal={confirmModal}
        setConfirmModal={setConfirmModal}
        handleBlock={handleBlock}
        handleRemoveTeacher={handleRemoveTeacher}
        handleBlockClass={handleBlockClass}
        handleRemoveClass={handleRemoveClass}
        formatText={formatText}
      />

      {/* Add Teacher Drawer */}
      <Activity mode={isAddTeacherOpen ? 'visible' : 'hidden'}>
        <AddTeacherDrawer
          isOpen={isAddTeacherOpen}
          setIsOpen={setIsAddTeacherOpen}
          onSuccess={fetchTeachers}
          teacherToEdit={editingTeacher}
          onClose={() => setEditingTeacher(null)}
        />
      </Activity>

      {/* Add Class Drawer */}
      <Activity mode={isAddClassOpen ? 'visible' : 'hidden'}>
        <AddClassDrawer
          isOpen={isAddClassOpen}
          setIsOpen={setIsAddClassOpen}
          onSuccess={fetchClasses}
          onClose={() => setEditingClass(null)}
          classToEdit={editingClass}
        />
      </Activity>
    </div>
  );
};

export default TeachersAndClasses;
